import styled from '@emotion/styled';
import { css } from '@emotion/core';
import Icon from '__components/Kizen/Icon';
import type { DataAdornmentVariant, IconAdornmentProps } from './types';
import { TextSpan } from 'app/typography';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';

export const StyledAdornmentContainer = styled.div<{ color?: string }>`
  color: ${({ color }) => color || 'var(--color-button-tertiary-pressed)'};
  display: flex;
  // we've got hardcoded 'relative' classNames in kds Button component
  // messing up with z-indexes and breaking the Table layout.
  // We can remove this in future once Table Layout is refactored.
  & button {
    position: static;
    & > span {
      position: static;
    }
  }
`;

export const StyledAdornmentWrapper = styled.div<{
  translateYValue?: string;
}>`
  ${({ translateYValue }) => css`
    width: 100%;
    display: flex;
    align-items: center;
    ${translateYValue
      ? css`
          transform: translateY(${translateYValue});
        `
      : ''}
  `};
`;

// centering adornments case by case to be compatable with legacy kizen typography
export const StyledAdornments = styled.div<{
  variant?: string;
  translateYValue?: string;
}>`
  display: flex;
  align-items: center;
  ${({ translateYValue }) =>
    translateYValue &&
    css`
      transform: translateY(${translateYValue});
    `}

  ${({ variant }) => {
    switch (variant) {
      case 'outline':
        return css`
          padding-left: 15px;
        `;
      case 'underline':
        return css`
          height: 29px;
          padding-left: 5px;
        `;
      case 'client_card':
        return css`
          padding-left: 10px;
          & button {
            height: 14px;
          }
        `;
      case 'inline':
        return css`
          padding-left: 5px;
        `;
      default:
        return '';
    }
  }}
`;

export const StyledIcon = styled(Icon)<IconAdornmentProps>`
  display: inline-flex;
  align-items: center;
  height: auto;
  width: auto;
  color: var(--color-button-tertiary-default);
  ${({ start, end }) => {
    if (start) {
      return css`
        margin-right: 10px;
      `;
    } else if (end) {
      return css`
        margin-left: 10px;
      `;
    } else {
      return '';
    }
  }}

  ${({ variant, icon }) => {
    const sizedDimension = icon === 'false' ? 'height' : 'width';
    const otherDimension = sizedDimension === 'height' ? 'width' : 'height';
    const size = variant === 'underline' ? 10 : 12;
    return css`
      && svg {
        position: relative;
        color: inherit;
        ${sizedDimension}: ${size}px;
        ${otherDimension}: auto;
      }
    `;
  }}
`;

export const StyledTextSpan = styled(TextSpan)<{
  variant?: DataAdornmentVariant;
  start?: boolean;
  end?: boolean;
}>`
  display: inline-flex;
  align-items: center;

  ${({ variant, start, end }) => {
    if (start) {
      return css`
        margin-right: 8px;
        margin-left: ${variant === 'underline' ? '0px' : '-2px'};
      `;
    } else if (end) {
      return css`
        margin-left: 8px;
        margin-right: ${variant === 'underline' ? '0px' : '-2px'};
      `;
    } else {
      return '';
    }
  }}
`;

export const StyledIconButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  // specifically to show below table curtains
  & > div,
  & i {
    position: static;
  }
`;

export const StyledOption = styled.div<{
  color?: string;
}>`
  display: flex;
  align-items: center;
  padding: 9px 12px;
  gap: 8px;
  cursor: pointer;
  &:hover {
    color: ${({ color }) => color || 'var(--color-button-tertiary-pressed)'};
  }
`;

export const StyledOptionLabel = styled(TextEllipsisWithTooltip)`
  color: inherit;
`;
